import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Autocomplete,
  TextField,
  CardContent,
  Card,
  Button,
  CardActions,
  Typography,
  Chip,
  Box,
} from '@mui/material';

// import { fetchOrganizations } from 'api';
import { fetchOrganizationById, patchUser } from 'api';
import { getErrorDetail } from 'helpers';
import { useCandidateAdminContext } from 'hooks';
import { useSnackContext } from 'hooks/useSnackContext';
import { Maybe, OrganizationProps, UserProps } from 'types';
import { Option } from 'types/Form';

export const UserOrgEdit = (props: {
  userId: number;
  organization: Maybe<OrganizationProps>;
  setUserState: React.Dispatch<React.SetStateAction<UserProps>>;
  setUserRefresh: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { setSnack } = useSnackContext();
  const { organization, setUserState, userId, setUserRefresh } = props;
  const [orgChildren, setOrgChildren] = useState<Array<OrganizationProps>>([]);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    async function fetchOrgChildren() {
      try {
        if (organization?.id) {
          const {
            data: { children },
          } = await fetchOrganizationById(organization.id);
          setOrgChildren(children);
        }
      } catch (e) {
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: 'Failed To Fetch Organization Children',
          error: e as Error,
        }));
      }
    }
    fetchOrgChildren();
  }, [organization?.id, setOrgChildren, setSnack]);

  const handleRemoveOrganization = useCallback(async () => {
    try {
      await patchUser(userId, {
        data: {
          organization_id: null,
        },
      });
      setUserState((prev) => ({
        ...prev,
        organization_id: null,
        organization: null,
      }));
      setSnack((props) => ({
        ...props,
        severity: 'success',
        open: true,
        message: 'Organization Removed',
      }));
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: getErrorDetail({
          error: e,
          fallback: 'Failed To Remove Organization',
        }),
        error: e as Error,
      }));
    }
  }, [setSnack, setUserState, userId]);

  const { organizationArray } = useCandidateAdminContext();

  const orgOptions = useMemo(() => {
    return organizationArray.length > 0
      ? organizationArray
          .sort((a, b) => a.name?.localeCompare(b.name))
          .map((org) => {
            return { label: org.name ?? '', value: org.id };
          })
      : [];
  }, [organizationArray]);

  const handleOrgChange = useCallback(
    async (
      _event: React.SyntheticEvent<Element, Event>,
      option: Option | null,
    ) => {
      try {
        if (option) {
          const { data } = await patchUser(userId, {
            data: {
              organization_id: option.value,
            },
          });
          setUserState((prevState) => ({ ...prevState, ...data }));
          setUserRefresh(true);
          setEditing(false);
        }
      } catch (e) {
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: 'Failed to Change Organization',
          error: e as Error,
        }));
      }
    },
    [setSnack, setUserRefresh, setUserState, userId],
  );

  return (
    <Card>
      <CardContent>
        <Box sx={{ mr: 2, ml: 2 }}>
          <Typography
            style={{ fontWeight: 'bold' }}
            color='primary.dark'
            variant='subtitle1'
          >
            ORGANIZATION
          </Typography>
          <Typography style={{ marginBottom: 18 }} variant='body1'>
            {organization?.name || 'None'}
          </Typography>
          {editing ? (
            <Autocomplete
              value={
                orgOptions.find((org) => org.value === organization?.id) || null
              }
              isOptionEqualToValue={(option: Option, value: Option) => {
                return option.value === value.value;
              }}
              onChange={handleOrgChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Search Groups'
                  variant='standard'
                />
              )}
              groupBy={(option) => option.label.charAt(0)}
              options={orgOptions}
            />
          ) : (
            <>
              <Typography
                style={{ fontWeight: 'bold' }}
                color='success.dark'
                variant='subtitle1'
              >
                ASSOCIATED ENTITIES
              </Typography>
              {orgChildren.length > 0 ? (
                orgChildren.map((org) => (
                  <Chip
                    key={org.id}
                    label={org.name}
                    component='a'
                    href={`/home/candidate_admin_dashboard/organizations/${org.id}`}
                    variant='outlined'
                    clickable
                  />
                ))
              ) : (
                <Typography variant='body1'>None</Typography>
              )}
            </>
          )}
        </Box>
      </CardContent>
      <CardActions>
        <Button size='small' onClick={() => setEditing(!editing)}>
          {editing ? 'Cancel' : 'Edit'}
        </Button>
        {!editing && organization && (
          <Button size='small' onClick={handleRemoveOrganization}>
            Remove Organization
          </Button>
        )}
      </CardActions>
    </Card>
  );
};
